import { scoreQueryOptions, scoreRefreshIntervalMs } from "./scoreQueryOptions";
/**
 * The `All` feed: one section per league -- the fan's pinned leagues first
 * in pin order, then every other league with a game in the window -- plus
 * the Your teams card and the cold-start frame for a fan with no pins and
 * no favorite teams. Two columns at `lg` and up. The league
 * chip row is mounted by the route above this pane, not here (ISSUE-949).
 * A failed catalog, profile or main feed read shows one failed line with Try
 * again in place of the whole feed; a failed favorites feed fails only the
 * Your teams card (ISSUE-1022).
 */
import { useFeedUpcoming } from "@sportsmet/events-sdk/private";
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Button } from "@sportsmet/ui";
import { type ReactNode } from "react";

import { queryApiErrorIdentity } from "../../errors/apiErrorBus";
import { collectFavoriteEvents } from "./favoriteScores";
import { FavoriteTeamsSectionCard } from "./FavoriteTeamsSectionCard";
import { LeagueSectionCard } from "./LeagueSectionCard";
import {
  COLD_START_BODY,
  COLD_START_TITLE,
  LEAGUE_MENU_OPEN_LABEL,
} from "./leagueSelectorContract";
import { resolveScoresHomeSections } from "./scoresHomeState";
import { ScoresSectionCard } from "./ScoresSectionCard";
import {
  ScoresSectionFailed,
  ScoresSectionLoading,
} from "./ScoresSectionStates";
import { useRaiseReadError } from "./useRaiseReadError";
import { resolveViewerTimeZone } from "./viewerTimeZone";

export type typeScoresHomePaneProps = {
  onSelectLeague: (leagueId: string) => void;
  onOpenMenu: () => void;
  onOpenGame?: ((eventId: string) => void) | undefined;
};

/** The section grid. Cards are 12 apart on a phone; two across from `lg`. */
const SECTION_GRID_CLASS =
  "grid grid-cols-1 items-start gap-3 lg:grid-cols-2 lg:gap-6";

const HOME_CATALOG_ERROR_IDENTITY = queryApiErrorIdentity(
  "scores-home",
  "league-catalog/get",
);
const HOME_PROFILE_ERROR_IDENTITY = queryApiErrorIdentity(
  "scores-home",
  "profile/get",
);
const HOME_FEED_ERROR_IDENTITY = queryApiErrorIdentity(
  "scores-home",
  "feed/upcoming",
  "all",
);
const HOME_FAVORITES_FEED_ERROR_IDENTITY = queryApiErrorIdentity(
  "scores-home",
  "feed/upcoming",
  "favorites",
);

export const ScoresHomePane = (props: typeScoresHomePaneProps): ReactNode => {
  const timeZone = resolveViewerTimeZone();

  const catalog = useLeagueCatalogGet({});
  const profile = useProfileGet({});
  const sports = catalog.data?.data?.sports ?? [];
  const pinnedLeagueIds = profile.data?.data?.profile?.followedLeagueIds ?? [];
  const favoriteTeamIds = profile.data?.data?.profile?.favoriteTeamIds ?? [];

  const allLeagueIds = sports.flatMap((sport) =>
    sport.leagues.map((league) => league.leagueId),
  );
  const comingSoonLeagueIds = new Set(
    sports.flatMap((sport) =>
      sport.leagues
        .filter((league) => league.visibilityState === "comingSoon")
        .map((league) => league.leagueId),
    ),
  );

  const feed = useFeedUpcoming(
    { leagueIds: allLeagueIds, perLeagueLimit: 5, windowDays: 1, timeZone },
    {
      enabled: allLeagueIds.length > 0,
      ...scoreQueryOptions,
      refetchInterval: (query) => {
        const seconds = query.state.data?.data?.refreshAfterSeconds ?? 0;
        return scoreRefreshIntervalMs(seconds);
      },
    },
  );

  // Your teams reads its own favorites-only feed over the same window as the
  // All feed above, so a favorite's game past the 5-per-league cap still shows
  // and no game outside the window can (ISSUE-041).
  const favoriteFeed = useFeedUpcoming(
    {
      leagueIds: allLeagueIds,
      teamIds: favoriteTeamIds,
      perLeagueLimit: 5,
      windowDays: 1,
      timeZone,
    },
    {
      enabled: allLeagueIds.length > 0 && favoriteTeamIds.length > 0,
      ...scoreQueryOptions,
      refetchInterval: (query) => {
        const seconds = query.state.data?.data?.refreshAfterSeconds ?? 0;
        return scoreRefreshIntervalMs(seconds);
      },
    },
  );

  const catalogError = useRaiseReadError({
    data: catalog.data,
    dataUpdatedAt: catalog.dataUpdatedAt,
    identity: HOME_CATALOG_ERROR_IDENTITY,
  });
  const profileError = useRaiseReadError({
    data: profile.data,
    dataUpdatedAt: profile.dataUpdatedAt,
    identity: HOME_PROFILE_ERROR_IDENTITY,
  });
  const feedError = useRaiseReadError({
    data: feed.data?.data != null ? undefined : feed.data,
    dataUpdatedAt: feed.dataUpdatedAt,
    identity: HOME_FEED_ERROR_IDENTITY,
  });
  const favoritesFeedError = useRaiseReadError({
    data: favoriteFeed.data?.data != null ? undefined : favoriteFeed.data,
    dataUpdatedAt: favoriteFeed.dataUpdatedAt,
    identity: HOME_FAVORITES_FEED_ERROR_IDENTITY,
  });

  const feedSections = feed.data?.data?.sections ?? [];
  const eventsByLeagueId = new Map(
    feedSections.map((section) => [section.leagueId, section.events]),
  );
  const leagueIdsWithGames = feedSections
    .filter((section) => section.events.length > 0)
    .map((section) => section.leagueId);

  const favoriteEvents = collectFavoriteEvents({
    sections: favoriteFeed.data?.data?.sections ?? [],
    favoriteTeamIds,
  });

  const leagueNameById = new Map(
    sports.flatMap((sport) =>
      sport.leagues.map((league) => [league.leagueId, league.shortName]),
    ),
  );

  const sections = resolveScoresHomeSections({
    sports,
    pinnedLeagueIds,
    leagueIdsWithGames,
  });

  const isInitialLoading = catalog.isLoading || profile.isLoading;

  const isCatalogFailed = catalog.isError || catalogError.errorCode !== null;
  const isProfileFailed = profile.isError || profileError.errorCode !== null;
  const isFeedFailed =
    feed.data?.data == null && (feed.isError || feedError.errorCode !== null);
  const isFavoritesFeedFailed =
    favoriteFeed.data?.data == null &&
    (favoriteFeed.isError || favoritesFeedError.errorCode !== null);

  const phase: "loading" | "failed" | "ready" = isInitialLoading
    ? "loading"
    : isCatalogFailed || isProfileFailed || isFeedFailed
      ? "failed"
      : "ready";

  // `!isProfileFailed` is checked directly, not only through `phase`: a
  // profile that fails while the catalog is still loading leaves `phase` on
  // "loading", and the pins read as empty, so without it a fan who has pins
  // would see the pin a league frame above the skeleton.
  const showColdStart =
    !profile.isLoading &&
    !isProfileFailed &&
    phase !== "failed" &&
    pinnedLeagueIds.length === 0 &&
    favoriteTeamIds.length === 0;

  // Refetch only the reads that failed: a disabled feed would fire with no
  // leagues. Once the catalog recovers, both feeds enable themselves. The
  // favorites feed is included because an events-api outage fails both feeds
  // at once, and one Try again has to bring back the whole home.
  const onRetry = (): void => {
    if (isCatalogFailed) {
      catalogError.forgetError();
      void catalog.refetch();
    }
    if (isProfileFailed) {
      profileError.forgetError();
      void profile.refetch();
    }
    if (isFeedFailed) {
      feedError.forgetError();
      void feed.refetch();
    }
    if (isFavoritesFeedFailed) {
      favoritesFeedError.forgetError();
      void favoriteFeed.refetch();
    }
  };

  const onRetryFavorites = (): void => {
    favoritesFeedError.forgetError();
    void favoriteFeed.refetch();
  };

  return (
    <div className="flex flex-col gap-3" data-testid="scores-home">
      {showColdStart ? (
        <div
          className="flex flex-col items-center gap-2 py-10 text-center"
          data-testid="scores-home-cold-start"
        >
          <h2 className="text-h1">{COLD_START_TITLE}</h2>
          <p className="text-body text-ink-secondary">{COLD_START_BODY}</p>
          <Button onClick={props.onOpenMenu} className="mt-2">
            {LEAGUE_MENU_OPEN_LABEL}
          </Button>
        </div>
      ) : null}

      {phase === "loading" ? (
        <div className={SECTION_GRID_CLASS} data-testid="scores-home-loading">
          {[0, 1].map((index) => (
            <ScoresSectionCard
              key={index}
              testId="scores-home-loading-card"
              title=""
            >
              <ScoresSectionLoading testId="scores-home-loading-rows" />
            </ScoresSectionCard>
          ))}
        </div>
      ) : phase === "failed" ? (
        <div
          className="overflow-hidden rounded-md border border-hairline bg-surface"
          data-testid="scores-home-failed"
        >
          <ScoresSectionFailed
            testId="scores-home-failed-body"
            onRetry={onRetry}
            hasDivider={false}
          />
        </div>
      ) : (
        <div className={SECTION_GRID_CLASS} data-testid="scores-home-sections">
          <FavoriteTeamsSectionCard
            events={favoriteEvents}
            hasFavorites={favoriteTeamIds.length > 0}
            isFeedLoading={favoriteFeed.isLoading}
            hasFailed={isFavoritesFeedFailed}
            onRetry={onRetryFavorites}
            onOpenGame={props.onOpenGame}
            favoriteTeamIds={favoriteTeamIds}
            leagueNameById={leagueNameById}
          />
          {sections.map((section) => (
            <LeagueSectionCard
              key={section.leagueId}
              leagueId={section.leagueId}
              leagueName={section.leagueName}
              timeZone={timeZone}
              events={eventsByLeagueId.get(section.leagueId) ?? []}
              isFeedLoading={feed.isLoading}
              isPinned={section.isPinned}
              isComingSoon={comingSoonLeagueIds.has(section.leagueId)}
              onOpenLeague={props.onSelectLeague}
              onOpenGame={props.onOpenGame}
              favoriteTeamIds={favoriteTeamIds}
            />
          ))}
        </div>
      )}
    </div>
  );
};
